// Presentation layer for the company industry classification.
//
// The backend decides the verdict (services/industryClassifier.js owns
// classifyFromSubstrate); this file only decides how it reads in the UI and the CSV.
//
// Why a second industry column. A search for "SaaS companies" returned Alterna Capital
// Solutions, an invoice-factoring lender, because Lead411 labels it "Financial Services" —
// exactly as it labels TrustPoint.ai, a genuine SaaS company from the same search. The raw
// "Industry" column stays and this sits beside it: the contrast between what Lead411 said
// and what the company says about itself is how a mislabel becomes visible at all.
//
// There is deliberately no match or source column in the export. Contacts whose industry
// contradicts the search are dropped before it, so every surviving row would say "Match", and
// naming the vendor that decided reads like a debug line rather than a product. The same
// argument retired the Source column: the export is a deliverable a customer opens, and
// "Lead411 (verified)" tells them about our supply chain, not about their prospect.
//
// The provenance still matters internally — a pattern-guessed address that ZeroBounce merely
// failed to reject is not the same evidence as an unlocked one — so `contact.source` is still
// set, still drives the row's badge and hover tooltip, and is still logged. It just is not a
// column in the file we hand over.

// "Industry (Specific)" — what this company actually is.
export function industrySpecificLabel(contact) {
  const rec = contact?.industryClass;
  if (!rec) return '';
  return rec.specificLabel || '';
}

// Whether the row deserves a badge in the table. A confirmed, confident match is the
// expected case and stays quiet — flagging everything would train people to ignore it.
export function shouldFlagIndustry(contact) {
  const rec = contact?.industryClass;
  if (!rec) return false;
  return rec.verdict !== 'match' || rec.confidence === 'low';
}

// Hover text for that badge: the label, the evidence behind it, and who said so.
export function industryTooltip(contact) {
  const rec = contact?.industryClass;
  if (!rec) return '';
  return [rec.specificLabel, rec.evidence, rec.source && `(${rec.source})`]
    .filter(Boolean)
    .join(' — ');
}
